import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Search, BarChart3, Wrench, TrendingUp } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TYPE_LABELS, TYPE_ICONS } from "./constants";
import { errMsg } from "../../lib/apiErrors";

export default function StatsView({ meta, items }) {
  const [search, setSearch] = useState("");
  const [repairStats, setRepairStats] = useState(null);
  const [loadingRepairs, setLoadingRepairs] = useState(false);

  useEffect(() => {
    setLoadingRepairs(true);
    api.get("/inventory/repairs/stats")
      .then((r) => setRepairStats(r.data))
      .catch((e) => {
        toast.error(errMsg(e, "Eroare statistici reparații"));
        setRepairStats(null);
      })
      .finally(() => setLoadingRepairs(false));
  }, [items.length]);

  // Donut: count per item_type
  const typeData = useMemo(() => {
    const m = {};
    for (const it of items) {
      const t = it.item_type || "echipament";
      const q = Number(it.quantity) > 0 ? Number(it.quantity) : 1;
      m[t] = (m[t] || 0) + q;
    }
    return Object.entries(m).map(([k, v]) => ({
      name: TYPE_LABELS[k] || k,
      value: v,
      key: k,
    }));
  }, [items]);

  const total = typeData.reduce((s, x) => s + x.value, 0);

  // Distribution per item name across locations
  const itemNameDist = useMemo(() => {
    const map = {};  // name -> { item_type, total, byLocation: { loc: count } }
    for (const it of items) {
      const n = (it.name || "—").trim();
      const loc = it.location || "—";
      const q = Number(it.quantity) > 0 ? Number(it.quantity) : 1;
      if (!map[n]) {
        map[n] = { name: n, item_type: it.item_type || "echipament", total: 0, byLocation: {} };
      }
      map[n].total += q;
      map[n].byLocation[loc] = (map[n].byLocation[loc] || 0) + q;
    }
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [items]);

  const filtered = useMemo(() => {
    if (!search) return itemNameDist;
    const q = search.toLowerCase();
    return itemNameDist.filter((r) => r.name.toLowerCase().includes(q));
  }, [itemNameDist, search]);

  const COLORS = { echipament: "#2563EB", consumabil: "#D97706", psa: "#0891B2" };

  return (
    <div className="space-y-4" data-testid="stats-view">
      {/* Top KPIs + Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="card-flat p-5 lg:col-span-1 flex flex-col">
          <div className="small-label">Total bucăți în firmă</div>
          <div className="font-display text-5xl font-extrabold text-zinc-800 mt-1">{total}</div>
          <div className="text-xs text-zinc-500 mt-1">{itemNameDist.length} denumiri distincte · {meta.locations.length} locații</div>
          <div className="mt-4 space-y-1.5">
            {typeData.map((t) => (
              <div key={t.key} className="flex items-center justify-between text-sm" data-testid={`stats-kpi-${t.key}`}>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm" style={{ background: COLORS[t.key] }}/>
                  <span className="text-xs text-zinc-700">{TYPE_ICONS[t.key]} {t.name}</span>
                </div>
                <div className="font-bold tabular-nums">{t.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card-flat p-5 lg:col-span-2">
          <div className="small-label mb-2">Distribuție stoc per tip</div>
          {total === 0 ? (
            <div className="h-64 flex items-center justify-center text-zinc-400 italic">Niciun articol în stoc.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={typeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                  {typeData.map((t) => (
                    <Cell key={t.key} fill={COLORS[t.key] || "#888"} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} buc`, n]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Reparații per categorie */}
      <RepairsStatsBlock data={repairStats} loading={loadingRepairs} />

      {/* Distribution table */}
      <div className="card-flat p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-[var(--primary)]"/>
            <h3 className="font-display text-lg font-bold">Distribuție per denumire</h3>
            <span className="text-xs text-zinc-500">({filtered.length})</span>
          </div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"/>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută denumire…"
              className="pl-7 h-8 w-56"
              data-testid="stats-search"
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <div className="text-sm text-zinc-400 italic text-center py-6">Niciun articol pentru această căutare.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stats-table">
              <thead className="bg-zinc-50 border-b border-[var(--border)]">
                <tr>
                  <th className="p-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 w-1/3">Denumire</th>
                  <th className="p-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 w-20">Tip</th>
                  <th className="p-2 text-right text-[10px] font-bold uppercase tracking-wider text-zinc-500 w-16">Total</th>
                  <th className="p-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500">Distribuție pe locații</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.name} className="border-b border-[var(--border)] last:border-b-0 hover:bg-zinc-50" data-testid={`stats-row-${r.name}`}>
                    <td className="p-2 font-semibold text-zinc-800">{r.name}</td>
                    <td className="p-2">
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${COLORS[r.item_type]}15`, color: COLORS[r.item_type] }}>
                        {TYPE_ICONS[r.item_type]} {TYPE_LABELS[r.item_type] || r.item_type}
                      </span>
                    </td>
                    <td className="p-2 text-right font-display text-base font-extrabold tabular-nums text-[var(--primary)]">{r.total}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(r.byLocation).sort((a, b) => b[1] - a[1]).map(([loc, n]) => (
                          <span key={loc} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">
                            <span className="text-zinc-500">{loc}</span> · <span className="font-bold tabular-nums">{n}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Reparații per categorie — insights pentru decizii înlocuire
// ============================================================
function RepairsStatsBlock({ data, loading }) {
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <div className="card-flat p-4">
        <div className="text-sm text-zinc-500 italic">Se încarcă statistici reparații...</div>
      </div>
    );
  }
  if (!data || !data.categories || data.totals.total_cost_chf === 0) {
    return (
      <div className="card-flat p-4">
        <div className="flex items-center gap-2 mb-2">
          <Wrench size={18} className="text-orange-600"/>
          <h3 className="font-display text-lg font-bold">Reparații — costuri pe categorie</h3>
        </div>
        <div className="text-sm text-zinc-400 italic">
          Nicio reparație înregistrată încă. Înregistrează reparații pe rândurile din tab-ul „Produse" (buton 🔧 log) pentru a vedea aici insights despre costuri.
        </div>
      </div>
    );
  }

  const withCosts = data.categories.filter((c) => c.total_cost_chf > 0);
  const shown = showAll ? withCosts : withCosts.slice(0, 8);
  const t = data.totals;

  return (
    <div className="card-flat p-4" data-testid="repairs-stats-block">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wrench size={18} className="text-orange-600"/>
          <h3 className="font-display text-lg font-bold">Reparații — costuri pe categorie</h3>
        </div>
        <div className="text-[11px] text-zinc-500">
          Generat: {new Date(data.generated_at).toLocaleString("ro-RO")}
        </div>
      </div>

      {/* KPIs top */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-3" data-testid="repairs-kpi-total">
          <div className="text-[10px] uppercase tracking-wider text-orange-800 font-bold">Cost total</div>
          <div className="font-display text-2xl font-extrabold text-orange-900 tabular-nums">
            {t.total_cost_chf.toFixed(2)}
          </div>
          <div className="text-[10px] text-orange-700">CHF</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-bold">Reparații</div>
          <div className="font-display text-2xl font-extrabold text-zinc-800 tabular-nums">
            {t.repairs_count}
          </div>
          <div className="text-[10px] text-zinc-500">total</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-bold">Echipamente</div>
          <div className="font-display text-2xl font-extrabold text-zinc-800 tabular-nums">
            {t.items_with_repairs}
          </div>
          <div className="text-[10px] text-zinc-500">cu reparații</div>
        </div>
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-bold">Categorii</div>
          <div className="font-display text-2xl font-extrabold text-zinc-800 tabular-nums">
            {withCosts.length}
          </div>
          <div className="text-[10px] text-zinc-500">cu cost &gt; 0</div>
        </div>
      </div>

      {/* Lista categorii */}
      <div className="space-y-1.5" data-testid="repairs-stats-list">
        <div className="grid grid-cols-[1fr_90px_90px_90px_1fr] gap-2 text-[10px] uppercase tracking-wider font-bold text-zinc-500 px-2 pb-1 border-b">
          <div>Categorie</div>
          <div className="text-right">Total CHF</div>
          <div className="text-right">{data.current_year}</div>
          <div className="text-right">Luna asta</div>
          <div>Cel mai costisitor echipament</div>
        </div>
        {shown.map((c) => {
          const slug = c.category.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          const heavy = c.total_cost_chf >= 500; // recomandare înlocuire dacă >500CHF
          return (
            <div
              key={c.category}
              className={`grid grid-cols-[1fr_90px_90px_90px_1fr] gap-2 items-center px-2 py-1.5 rounded text-sm transition-colors ${heavy ? "bg-red-50 border border-red-200" : "bg-white border border-zinc-100"}`}
              data-testid={`repairs-stats-row-${slug}`}
            >
              <div className="font-semibold flex items-center gap-1.5 truncate">
                <span className="truncate">{c.category}</span>
                <span className="text-[10px] text-zinc-500">({c.repairs_count} rep · {c.items_with_repairs}/{c.items_count} ech)</span>
                {heavy && (
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-red-200 text-red-900 px-1 py-0.5 rounded inline-flex items-center gap-0.5">
                    <TrendingUp size={9}/> ANALIZĂ
                  </span>
                )}
              </div>
              <div className={`text-right tabular-nums font-bold ${heavy ? "text-red-700" : "text-orange-700"}`}>
                {c.total_cost_chf.toFixed(2)}
              </div>
              <div className="text-right tabular-nums text-zinc-700">
                {c.cost_current_year > 0 ? c.cost_current_year.toFixed(2) : "—"}
              </div>
              <div className="text-right tabular-nums text-zinc-700">
                {c.cost_current_month > 0 ? c.cost_current_month.toFixed(2) : "—"}
              </div>
              <div className="text-[11px] text-zinc-600 truncate">
                {c.worst_item ? (
                  <>
                    <span className="font-semibold text-zinc-800">{c.worst_item.name}</span>
                    <span className="text-zinc-500 ml-1">({c.worst_item.repairs_count} rep, {c.worst_item.total_cost.toFixed(0)} CHF)</span>
                  </>
                ) : "—"}
              </div>
            </div>
          );
        })}
        {withCosts.length > 8 && (
          <button
            type="button"
            onClick={() => setShowAll((s) => !s)}
            className="w-full text-xs text-[var(--primary)] hover:underline py-1.5"
            data-testid="repairs-stats-toggle-all"
          >
            {showAll ? `↑ Arată mai puține` : `↓ Arată toate cele ${withCosts.length} categorii`}
          </button>
        )}
      </div>

      <div className="mt-3 text-[10px] text-zinc-500 italic">
        💡 Categoriile cu cost ≥ 500 CHF sunt marcate cu „ANALIZĂ" — consideră dacă merită înlocuirea în loc de reparare în continuare.
      </div>
    </div>
  );
}
